import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_HERO_IMAGE = "https://images.unsplash.com/photo-1697939829612-f2ee98421a53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxkb2clMjBvd25lciUyMG1hdGNoaW5nJTIwb3V0Zml0JTIwc3dlYXRlciUyMGZhc2hpb258ZW58MHx8fHwxNzcyMjI0MDAzfDA&ixlib=rb-4.1.0&q=85";

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  }
  return url;
};

export const HeroSection = () => {
  const [heroImage, setHeroImage] = useState(DEFAULT_HERO_IMAGE);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data?.hero_image) {
          setHeroImage(getImageUrl(response.data.hero_image));
        }
      } catch (error) {
        console.log('Using default hero image');
      }
    };
    fetchSettings();
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center bg-[#f5f0e6] overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 rounded-full border-2 border-[#1e3a5f]" />
        <div className="absolute bottom-40 right-20 w-48 h-48 rounded-full border-2 border-[#c68b4d]" />
        <div className="absolute top-1/2 left-1/3 w-24 h-24 rounded-full border-2 border-[#e76f51]" />
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="order-2 lg:order-1 text-left">
            <div className="inline-flex items-center gap-2 bg-[#c68b4d]/10 px-4 py-2 rounded-full mb-6 animate-fade-in-up">
              <Sparkles className="w-4 h-4 text-[#c68b4d]" />
              <span className="font-body text-sm font-medium text-[#c68b4d]">
                New Collection Available
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="font-heading text-5xl md:text-7xl font-bold tracking-tight leading-[1.1] text-[#1e3a5f] mb-6 animate-fade-in-up stagger-1"
            >
              Matching Fits for{' '}
              <span className="text-[#e76f51]">You</span> &{' '}
              <span className="text-[#c68b4d]">Your Best Friend</span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="font-body text-lg md:text-xl leading-relaxed text-[#1e3a5f]/70 mb-8 max-w-lg animate-fade-in-up stagger-2"
            >
              Premium quality apparel designed for the bond you share. Look stylish together, because your best friend deserves to match.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up stagger-3">
              <Link to="/shop?category=matching">
                <Button
                  data-testid="shop-matching-btn"
                  className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  Shop Matching Sets
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  data-testid="our-story-btn"
                  variant="outline"
                  className="border-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white rounded-full px-8 py-6 text-base font-semibold transition-all"
                >
                  Our Story
                </Button>
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex items-center gap-6 mt-10 animate-fade-in-up stagger-4">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#c68b4d]/10 rounded-full flex items-center justify-center">
                  <span className="text-lg">🐕</span>
                </div>
                <span className="font-body text-sm text-[#1e3a5f]/70">10K+ Happy Pups</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-[#c68b4d]/10 rounded-full flex items-center justify-center">
                  <span className="text-lg">⭐</span>
                </div>
                <span className="font-body text-sm text-[#1e3a5f]/70">4.9/5 Rating</span>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="order-1 lg:order-2 relative animate-fade-in-up">
            <div className="relative">
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-full h-full bg-[#c68b4d]/20 rounded-3xl" />
              <div className="absolute -bottom-4 -left-4 w-full h-full bg-[#1e3a5f]/10 rounded-3xl" />
              
              {/* Main image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={heroImage}
                  alt="Dog in stylish sweater"
                  className="w-full h-[500px] lg:h-[600px] object-cover"
                  data-testid="hero-image"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a5f]/20 to-transparent" />
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#e76f51] rounded-full flex items-center justify-center">
                    <span className="text-white text-xl">❤️</span>
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-[#1e3a5f]">Best Sellers</p>
                    <p className="font-body text-sm text-[#1e3a5f]/60">Crew Collection</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
